import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  Typography,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  styled,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useHistory } from "react-router-dom";
import { fetchMachineData } from "./api/machineApi";

const StatusBox = styled(Box)(({ status }) => ({
  borderRadius: "4px",
  padding: "4px 8px",
  fontWeight: "bold",
  fontSize: "0.8rem",
  display: "inline-block",
  minWidth: "70px",
  textAlign: "center",
  ...(status === "active" && {
    backgroundColor: "#4CAF50",
    color: "white",
  }),
  ...(status === "inactive" && {
    backgroundColor: "#F44336",
    color: "white",
  }),
}));

const OverviewItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isFirst",
})(({ theme, isFirst }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  gap: "70px",
  flexGrow: 1,
  textAlign: "center",
  backgroundColor: "#1b5e20",
  padding: "4px",
  borderRadius: "2px",
  color: "white",
  ...(isFirst
    ? {}
    : {
        borderLeft: "1px solid white",
      }),
}));

const MachineOverview = () => {
  const [machinesData, setMachinesData] = useState([]);
  const [activeMachines, setActiveMachines] = useState(0);
  const [inactiveMachines, setInactiveMachines] = useState(0);
  const [totalMachines, setTotalMachines] = useState(0);
  const history = useHistory();

  useEffect(() => {
    const getMachineData = async () => {
      try {
        const data = await fetchMachineData();
        setMachinesData(data);
        setTotalMachines(data.length);
        setActiveMachines(
          data.filter(
            (machine) => machine.trackerDevice.trackerDeviceStatus === "active"
          ).length
        );
        setInactiveMachines(
          data.filter(
            (machine) =>
              machine.trackerDevice.trackerDeviceStatus === "inactive"
          ).length
        );
      } catch (error) {
        console.error("Error fetching machine data:", error);
      }
    };

    getMachineData();
  }, []);

  const handleRegisterClick = () => {
    history.push("/register-machine");
  };

  const handleAssignMachineToDealer = () => {
    history.push("/assign-machine-to-dealer");
  };

  const handleAssignMachineToCustomer = () => {
    history.push("/assign-machine-to-customer");
  };

  return (
    <Box>
      <Box
        sx={{
          backgroundColor: "#1b5e20",
          padding: "16px",
          display: "flex",
          gap: "20px",
          alignItems: "center",
          marginBottom: "20px",
          borderRadius: "4px",
        }}
      >
        <OverviewItem isFirst>
          <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
            Total Machines
          </Typography>
          <Typography variant="h6">{totalMachines}</Typography>
        </OverviewItem>
        <OverviewItem>
          <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
            Active Machines
          </Typography>
          <Typography variant="h6">{activeMachines}</Typography>
        </OverviewItem>
        <OverviewItem>
          <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
            Inactive Machines
          </Typography>
          <Typography variant="h6">{inactiveMachines}</Typography>
        </OverviewItem>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          padding: "0 16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid #ccc",
            borderRadius: "4px",
            paddingLeft: "8px",
          }}
        >
          <SearchIcon color="action" />
          <TextField
            placeholder="Search here..."
            variant="standard"
            size="small"
            sx={{
              ml: 1,
              "& .MuiInput-underline:before": { borderBottom: "none" },
              "& .MuiInput-underline:after": { borderBottom: "none" },
            }}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            style={{ borderRadius: "4px" }}
            onClick={handleRegisterClick}
          >
            Register Machine
          </Button>
          <Button
            variant="contained"
            color="success"
            style={{ borderRadius: "4px" }}
            onClick={handleAssignMachineToDealer}
          >
            Assign Machine To Dealer
          </Button>
          <Button
            variant="contained"
            color="success"
            style={{ borderRadius: "4px" }}
            onClick={handleAssignMachineToCustomer}
          >
            Assign Machine To Customer
          </Button>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        style={{ borderRadius: "4px", margin: "0 16px" }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="machine overview table">
          <TableHead style={{ backgroundColor: "#1b5e20" }}>
            <TableRow>
              <TableCell style={{ fontWeight: "bold", color: "white" }}>
                Name
              </TableCell>
              <TableCell style={{ fontWeight: "bold", color: "white" }}>
                Machine Name
              </TableCell>
              <TableCell style={{ fontWeight: "bold", color: "white" }}>
                Machine Type
              </TableCell>
              <TableCell style={{ fontWeight: "bold", color: "white" }}>
                Machine Variant
              </TableCell>
              <TableCell style={{ fontWeight: "bold", color: "white" }}>
                Description
              </TableCell>
              <TableCell style={{ fontWeight: "bold", color: "white" }}>
                Tractor HP Required
              </TableCell>
              <TableCell style={{ fontWeight: "bold", color: "white" }}>
                Tracker Device Name
              </TableCell>
              <TableCell style={{ fontWeight: "bold", color: "white" }}>
                Device Status
              </TableCell>
              <TableCell style={{ fontWeight: "bold", color: "white" }}>
                Assigned to Dealer
              </TableCell>
              <TableCell style={{ fontWeight: "bold", color: "white" }}>
                Assigned to Customer
              </TableCell>
              <TableCell
                align="right"
                style={{ fontWeight: "bold", color: "white" }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {machinesData.map((machine) => (
              <TableRow
                key={machine.id}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell>{machine.manufacturer.name}</TableCell>
                <TableCell>{machine.machineName}</TableCell>
                <TableCell component="th" scope="row">
                  {machine.machineType.machineTypeName}
                </TableCell>
                <TableCell>
                  {machine.machineVariant.machineVariantName}
                </TableCell>
                <TableCell style={{ color: "#757575" }}>
                  {machine.description}
                </TableCell>
                <TableCell>{machine.tractorHorsePower}</TableCell>
                <TableCell>
                  {machine.trackerDevice.trackerDeviceBrand}
                </TableCell>
                <TableCell>
                  <StatusBox status={machine.trackerDevice.trackerDeviceStatus}>
                    {machine.trackerDevice.trackerDeviceStatus}
                  </StatusBox>
                </TableCell>
                <TableCell>{machine.dealer.dealerName}</TableCell>
                <TableCell>{machine.customer.name}</TableCell>
                <TableCell align="right">
                  <Tooltip title="View">
                    <IconButton aria-label="view" size="small">
                      <VisibilityIcon color="action" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton aria-label="edit" size="small">
                      <EditIcon color="primary" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton aria-label="delete" size="small">
                      <DeleteIcon color="error" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MachineOverview;
